import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Spinner, Alert } from 'react-bootstrap'
import Select from 'react-select'
import { useNavigate } from 'react-router-dom'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import '../styles/selectControl.css'
import '../styles/automationCard.css'

function AmazonAdAutomationDashboardScreen() {
    const navigate = useNavigate()

    const [accounts, setAccounts] = useState([])
    const [selectedAccountId, setSelectedAccountId] = useState('')
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)
    const [summary, setSummary] = useState(null)

    useEffect(() => {
        const loadAccounts = async () => {
            try {
                const data = await amazonAPI.accounts.getAll()
                setAccounts(data || [])
                if (data?.length === 1) {
                    setSelectedAccountId(data[0].id)
                }
            } catch (err) {
                setError(formatErrorResponse(err))
            }
        }
        loadAccounts()
    }, [])

    useEffect(() => {
        if (!selectedAccountId) {
            setSummary(null)
            return
        }
        const loadSummary = async () => {
            setLoading(true)
            try {
                const data = await amazonAPI.bidding.getDashboardSummary(selectedAccountId)
                setSummary(data)
                setError('')
            } catch (err) {
                setError(formatErrorResponse(err))
                setSummary(null)
            } finally {
                setLoading(false)
            }
        }
        loadSummary()
    }, [selectedAccountId])

    const accountOptions = accounts.map(a => ({ value: a.id, label: a.account_name }))

    const handleCardClick = (path) => {
        if (!selectedAccountId) return
        const account = accounts.find(a => a.id === selectedAccountId)
        navigate(path, {
            state: {
                accountId: selectedAccountId,
                accountName: account?.account_name || '',
            }
        })
    }

    const total = summary ? summary.ok_count + summary.decrease_count + summary.error_count : 0
    const okPct = total > 0 ? (summary.ok_count / total) * 100 : 0
    const decreasePct = total > 0 ? (summary.decrease_count / total) * 100 : 0
    const errorPct = total > 0 ? (summary.error_count / total) * 100 : 0

    return (
        <Container className="py-4">
            <h1 className="mb-4">Ad Automation</h1>

            <Row className="mb-4">
                <Col md={4}>
                    <Select
                        value={accountOptions.find(opt => opt.value === selectedAccountId) || null}
                        onChange={(opt) => setSelectedAccountId(opt?.value || '')}
                        options={accountOptions}
                        placeholder="Select Account..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        isClearable
                    />
                </Col>
            </Row>

            {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}

            {!selectedAccountId && (
                <p className="text-muted">Select an account to view automation options.</p>
            )}

            {loading && (
                <div className="text-center py-5">
                    <Spinner animation="border" />
                </div>
            )}

            {selectedAccountId && !loading && (
                <Row className="g-4">
                    <Col lg={6}>
                        <div
                            className="automation-card"
                            role="button"
                            tabIndex={0}
                            onClick={() => handleCardClick('/amazon-ad-automation/bidding')}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleCardClick('/amazon-ad-automation/bidding') }}
                        >
                            <div className="icon-rail">
                                <i className="fab fa-amazon"></i>
                            </div>
                            <div className="card-content">
                                <div>
                                    <div className="top-row">
                                        <div className="title-block">
                                            <h5>Inventory-Aware Bidding</h5>
                                            <p className="desc">Monitors FBA inventory against sales velocity to detect low-stock risk. When stock drops below a critical threshold, recommends decreasing ad bids to slow sales and prevent out-of-stock, protecting organic search ranking.</p>
                                        </div>
                                        {summary && summary.enrolled_count > 0 && (
                                            <span className="enrolled-badge">{summary.enrolled_count} Enrolled</span>
                                        )}
                                    </div>
                                    {summary && summary.enrolled_count === 0 && (
                                        <p className="text-muted mb-0" style={{ fontSize: '0.8rem' }}>No campaigns enrolled yet</p>
                                    )}
                                </div>
                                {summary && total > 0 && (
                                    <div>
                                        <div className="mini-bar-track">
                                            {okPct > 0 && <div className="mini-bar-ok" style={{ width: `${okPct}%` }}></div>}
                                            {decreasePct > 0 && <div className="mini-bar-decrease" style={{ width: `${decreasePct}%` }}></div>}
                                            {errorPct > 0 && <div className="mini-bar-error" style={{ width: `${errorPct}%` }}></div>}
                                        </div>
                                        <div className="legend">
                                            <span className="legend-item">
                                                <span className="legend-dot" style={{ background: '#198754' }}></span>
                                                <span className="legend-count">{summary.ok_count}</span> OK
                                            </span>
                                            <span className="legend-item">
                                                <span className="legend-dot" style={{ background: '#dc3545' }}></span>
                                                <span className="legend-count">{summary.decrease_count}</span> Decrease
                                            </span>
                                            {summary.error_count > 0 && (
                                                <span className="legend-item">
                                                    <span className="legend-dot" style={{ background: '#adb5bd' }}></span>
                                                    <span className="legend-count">{summary.error_count}</span> Errors
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            )}
        </Container>
    )
}

export default AmazonAdAutomationDashboardScreen
